/**
 * ApprovalsPanel — admin-side moderation queue for pending driver
 * applications (Phase 1 launch-readiness slice 1).
 *
 * Strict isolation contract:
 *   - Pure UI surface; no dispatch / state-machine / janitor / offer-routing
 *     code is touched anywhere in this file.
 *   - Reads from GET /api/admin/drivers/pending.
 *   - Approves via POST /api/admin/drivers/{id}/approve.
 *   - Rejects via POST /api/admin/drivers/{id}/reject (requires reason).
 *   - After every mutation we re-fetch the queue so the row vanishes
 *     immediately — this is cheaper than maintaining a local cache and
 *     guarantees the UI reflects whatever the backend audited.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RefreshCw, Mail, Phone, Car, Clock, FileText } from "lucide-react";
import { DocumentReviewer } from "./DocumentReviewer";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
};

export const ApprovalsPanel = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Inline reject-reason form per driver — the operator types the reason
  // beside the driver's row rather than in a modal so a fleet of pending
  // applications can be processed quickly.
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  // Per-row toggle for the document-review subsection. Only one row's
  // docs are loaded at a time to keep base64 traffic minimal.
  const [docsOpenId, setDocsOpenId] = useState(null);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/drivers/pending");
      setPending(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load pending drivers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleApprove = async (driver) => {
    if (busyId) return;
    setBusyId(driver.id);
    try {
      await api.post(`/admin/drivers/${driver.id}/approve`, { notify: true });
      toast.success(`${driver.name} approved — they've been emailed.`);
      // Optimistic local removal so the row disappears immediately, then
      // a quiet re-fetch reconciles in case of concurrent admin edits.
      setPending((prev) => prev.filter((d) => d.id !== driver.id));
      fetchPending();
    } catch (e) {
      toast.error(formatApiError(e) || "Approval failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (driver) => {
    if (busyId) return;
    if (!rejectReason.trim() || rejectReason.trim().length < 2) {
      toast.error("Please enter a reason for rejection.");
      return;
    }
    setBusyId(driver.id);
    try {
      await api.post(`/admin/drivers/${driver.id}/reject`, {
        reason: rejectReason.trim(), notify: true,
      });
      toast.success(`${driver.name} rejected — they've been notified.`);
      setPending((prev) => prev.filter((d) => d.id !== driver.id));
      setRejectingId(null);
      setRejectReason("");
      fetchPending();
    } catch (e) {
      toast.error(formatApiError(e) || "Rejection failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="approvals-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-black tracking-tight">Driver Approvals</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Review new driver applications. Approved drivers receive an email
            and can immediately go online; rejected drivers see the reason in
            their dashboard banner.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchPending} disabled={loading} data-testid="approvals-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {loading && pending.length === 0 && (
        <div className="text-center py-12 text-muted-foreground text-sm">
          Loading pending applications…
        </div>
      )}

      {!loading && pending.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center" data-testid="approvals-empty">
          <CheckCircle2 className="w-10 h-10 mx-auto text-[#10B981] mb-2" />
          <div className="font-heading text-lg font-bold">Inbox zero.</div>
          <p className="text-sm text-muted-foreground mt-1">
            No driver applications awaiting review. New registrations land here automatically.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {pending.map((d) => (
          <div
            key={d.id}
            className="border border-border p-4 transition-card"
            data-testid={`pending-driver-${d.id}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-heading text-lg font-black">{d.name}</span>
                  {d.docs_review_only ? (
                    <span className="label-mini text-[#2563EB] flex items-center gap-1" data-testid={`docs-to-review-${d.id}`}>
                      <FileText className="w-3 h-3" />
                      Documents to review
                    </span>
                  ) : (
                    <span className="label-mini text-[#F59E0B] flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Applied {fmtDate(d.created_at)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {d.email}</div>
                  {d.phone && (
                    <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {d.phone}</div>
                  )}
                  {(d.vehicle_make || d.vehicle_plate) && (
                    <div className="flex items-center gap-1.5">
                      <Car className="w-3 h-3" /> {d.vehicle_make || "—"} {d.vehicle_plate ? `· ${d.vehicle_plate}` : ""}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                {!d.docs_review_only && <Button
                  size="sm" onClick={() => handleApprove(d)}
                  disabled={busyId === d.id || !d.compliance_met}
                  title={d.compliance_met
                    ? "Approve this driver"
                    : "All required documents must be approved before this driver can be approved"}
                  className="bg-[#10B981] hover:bg-[#059669] text-white disabled:opacity-50"
                  data-testid={`approve-driver-${d.id}`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  {busyId === d.id ? "…" : "Approve"}
                </Button>}
                {!d.docs_review_only && <span
                  className="label-mini text-center"
                  style={{ color: d.compliance_met ? "#065F46" : "#92400E" }}
                  data-testid={`compliance-status-${d.id}`}
                >
                  {d.docs_approved ?? 0}/{d.docs_required ?? 5} docs approved
                </span>}
                <Button
                  size="sm" variant="outline"
                  onClick={() => setDocsOpenId(docsOpenId === d.id ? null : d.id)}
                  className="border-border"
                  data-testid={`view-docs-${d.id}`}
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5" />
                  {docsOpenId === d.id ? "Hide docs" : "View docs"}
                </Button>
                {!d.docs_review_only && <Button
                  size="sm" variant="outline"
                  onClick={() => {
                    setRejectingId(rejectingId === d.id ? null : d.id);
                    setRejectReason("");
                  }}
                  disabled={busyId === d.id}
                  className="border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2]"
                  data-testid={`reject-driver-${d.id}`}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1.5" />
                  Reject
                </Button>}
              </div>
            </div>
            {rejectingId === d.id && !d.docs_review_only && (
              <div
                className="mt-3 pt-3 border-t border-border space-y-2"
                data-testid={`reject-form-${d.id}`}
              >
                <Label htmlFor={`reason-${d.id}`} className="label-mini">REJECTION REASON</Label>
                <Input
                  id={`reason-${d.id}`}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g. Vehicle plate doesn't match licence — please re-apply with matching details."
                  maxLength={500}
                  data-testid={`reject-reason-${d.id}`}
                />
                <div className="text-[10px] text-muted-foreground">
                  This wording is shown to the driver verbatim in their dashboard banner and email.
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => { setRejectingId(null); setRejectReason(""); }}
                    data-testid={`reject-cancel-${d.id}`}
                  >Cancel</Button>
                  <Button
                    size="sm" onClick={() => handleReject(d)}
                    disabled={busyId === d.id || rejectReason.trim().length < 2}
                    className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
                    data-testid={`reject-confirm-${d.id}`}
                  >
                    {busyId === d.id ? "Sending…" : "Confirm rejection"}
                  </Button>
                </div>
              </div>
            )}
            {docsOpenId === d.id && <DocumentReviewer driverId={d.id} onDocChange={fetchPending} />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ApprovalsPanel;
